import {
  AssociationRecord,
  AssociationType,
} from "@/shared/domain/association";
import { TopicRecord } from "@/shared/domain/topics";
import {
  resolveSignalEntries,
  SignalEntrySource,
} from "@/shared/domain/workspaces/resolveSignalEntries";
import { SignalEntry } from "@/shared/domain/workspaces/signalValidation";
import { LoggerService } from "@/shared/services/LoggerService";

import { EventRecord } from "./EventRecord";
import { groupAssociationsByType } from "./groupAssociationsByType";

/**
 * The datasources and panel signals an event's associations resolve to.
 */
export interface EventScope {
  /** Datasets the event is directly associated with, deduplicated. */
  datasetIds: string[];
  /**
   * Files the event's associations resolve to, deduplicated: direct file
   * associations plus the backing files of topic and message-path
   * associations.
   */
  fileIds: string[];
  /**
   * The event's message-path associations resolved to signal entries,
   * deduplicated and limited to file-backed topics. Feed these to
   * `buildEventScopeLayout` to pre-build panels for the event's scope.
   */
  scopedMessagePaths: SignalEntry[];
}

/**
 * Resolve an event's associations to the full scope of data it covers:
 * dataset associations contribute their dataset, file associations their
 * file, and topic and message-path associations the file backing their
 * topic.
 *
 * Associations that fail to resolve (deleted topics, missing permissions,
 * topics without a backing file) are logged and skipped so the rest still
 * load. An event whose associations all fail resolves to an empty scope
 * rather than throwing — how to treat emptiness is the caller's policy.
 */
export async function resolveEventScope(
  event: EventRecord,
  topics: SignalEntrySource,
): Promise<EventScope> {
  const associations = groupAssociationsByType(event);

  const datasetIds = Array.from(
    new Set(
      associations[AssociationType.Dataset].map(
        (association) => association.association_id,
      ),
    ),
  );

  const directFileIds = associations[AssociationType.File].map(
    (association) => association.association_id,
  );

  const [topicFileIds, scopedMessagePaths] = await Promise.all([
    resolveTopicFiles(topics, associations[AssociationType.Topic]),
    resolveEventMessagePathScope(event, topics),
  ]);

  const messagePathFileIds = scopedMessagePaths.map((scopedPath) =>
    backingFileId(scopedPath.topic),
  );

  const fileIds = Array.from(
    new Set([...directFileIds, ...topicFileIds, ...messagePathFileIds]),
  );

  return { datasetIds, fileIds, scopedMessagePaths };
}

/**
 * Resolve just the event's message-path associations to signal entries,
 * deduplicated and keeping only paths whose topic has a backing file.
 * Failures are logged and skipped. This is the message-path portion of
 * {@link resolveEventScope}, exposed for callers whose datasources are
 * already known and only need the panel signals.
 */
export async function resolveEventMessagePathScope(
  event: EventRecord,
  topics: SignalEntrySource,
): Promise<SignalEntry[]> {
  const associations = groupAssociationsByType(event);
  const messagePathIds = Array.from(
    new Set(
      associations[AssociationType.MessagePath].map(
        (association) => association.association_id,
      ),
    ),
  );

  const { entries, invalid } = await resolveSignalEntries(
    topics,
    messagePathIds,
  );
  invalid.forEach(({ messagePathId, reason }) => {
    LoggerService.error(
      `Skipping unresolvable event association ${messagePathId}: ${reason}`,
    );
  });

  return entries.filter((entry) => {
    try {
      // Called for its throw; callers recompute the id when they need it.
      backingFileId(entry.topic);
      return true;
    } catch (err) {
      LoggerService.error(
        `Skipping unresolvable event association ${entry.messagePath.message_path_id}`,
        err,
      );
      return false;
    }
  });
}

/**
 * The id of the file backing a topic. Throws for a topic associated with
 * something other than a file (a dataset, for example).
 */
export function backingFileId(topic: TopicRecord): string {
  if (topic.association.association_type !== AssociationType.File) {
    throw new Error(`Topic ${topic.topic_id} is not associated with a file`);
  }
  return topic.association.association_id;
}

/**
 * Resolve topic associations to their backing file ids, keeping resolved
 * ids in input order. Failures (deleted topics, topics not backed by a
 * file) are logged and skipped.
 */
async function resolveTopicFiles(
  topics: SignalEntrySource,
  topicAssociations: AssociationRecord[],
): Promise<string[]> {
  const settled = await Promise.allSettled(
    topicAssociations.map(async (association) => {
      const topic = await topics.getTopicById(association.association_id);
      return backingFileId(topic);
    }),
  );

  const fileIds: string[] = [];
  settled.forEach((result, i) => {
    if (result.status === "fulfilled") {
      fileIds.push(result.value);
    } else {
      LoggerService.error(
        `Skipping unresolvable event association ${topicAssociations[i].association_id}`,
        result.reason,
      );
    }
  });
  return fileIds;
}
